import { Suspense, lazy, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
// form
import { useFormContext, Controller } from "react-hook-form";
// ui
import { InlineError } from "@shopify/polaris";

// components
import Loader from "../../Loader";

const Editor = lazy(() => import("../../editor/v2"));

const EditorComponent = ({ field, id, error, ...props }) => {
  const editorRef = useRef(null);
  const previousValueRef = useRef(field.value);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Delay the hidden input initialization to allow React Quill to normalize the HTML
    const timer = setTimeout(() => {
      const hiddenInput = document.getElementById(`${id}-hidden`);
      if (hiddenInput) {
        hiddenInput.value = field.value;
        isInitializedRef.current = true;
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, []); // Only run on mount

  const handleEditorChange = (value) => {
    field.onChange(value);
    previousValueRef.current = value;

    // Always update hidden input, but only dispatch change event after initialization
    const hiddenInput = document.getElementById(`${id}-hidden`);
    if (hiddenInput) {
      hiddenInput.value = value;
      if (isInitializedRef.current) {
        hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  };

  return (
    <Suspense fallback={<Loader />}>
      <>
        <Editor
          ref={editorRef}
          id={id}
          value={field.value}
          onChange={handleEditorChange}
          error={error}
          preserveFocus={true}
          {...props}
        />
        <input id={`${id}-hidden`} type="text" defaultValue={field.value} style={{ display: "none" }} />
      </>
    </Suspense>
  );
};

EditorComponent.propTypes = {
  field: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  error: PropTypes.string,
};

export default function RHFEditor({ name, id = "editor", ...other }) {
  const { control, formState } = useFormContext();
  const [editorKey, setEditorKey] = useState(Date.now());
  const wasDirtyRef = useRef(false);

  // Only force remount when going from dirty to clean state (after successful submission)
  // This prevents losing focus when editing after save
  useEffect(() => {
    if (formState.isDirty) {
      wasDirtyRef.current = true;
    } else if (wasDirtyRef.current && !formState.isDirty) {
      // Only regenerate key when form goes from dirty to clean (after successful save)
      setEditorKey(Date.now());
      wasDirtyRef.current = false;
    }
  }, [formState.isDirty, formState.isSubmitSuccessful]);

  // Generate a unique ID for the toolbar that won't conflict with form field names
  const toolbarId = `toolbar-${id}`;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <EditorComponent
            key={editorKey}
            field={field}
            id={toolbarId}
            error={error ? error.message : undefined}
            {...other}
          />
          {error && <InlineError message={error.message} fieldID={id} />}
        </>
      )}
    />
  );
}

RHFEditor.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
};
